<template>
  <div class="add-or-edit-group">
    <el-form
      ref="formRef"
      :model="currentGroup"
      :rules="rules"
      label-width="90px"
    >
      <el-form-item label="小组名称" prop="groupName">
        <el-input
          v-model.trim="currentGroup.groupName"
          maxlength="20"
          show-word-limit
          placeholder="请输入小组名称"
          clearable
        />
      </el-form-item>

      <el-form-item label="成员信息" prop="memberWorkNumbers" class="member-form-item">
        <div class="member-toolbar">
          <el-link
            type="primary"
            :underline="false"
            @click="showMemberPanel = true; showSearchList = true; searchKey = ''"
          >
            添加成员
          </el-link>
        </div>

        <!-- 员工搜索面板 -->
        <div v-show="showMemberPanel" class="member-search-panel" ref="memberSearchPanelRef">
          <div class="member-search-header">
            <el-input
              v-model="searchKey"
              size="small"
              placeholder="请输入员工姓名/工号搜索后选择（可多选）"
              clearable
              prefix-icon="el-icon-search"
              @focus="showSearchList = true"
              ref="searchInputRef"
            />
            <el-button
              type="text"
              class="collapse-btn"
              @click="showMemberPanel = false; showSearchList = false; searchKey = ''"
            >收起</el-button>
          </div>
          <div v-show="showSearchList" class="member-search-list">
            <template v-if="filteredEmployees.length > 0">
              <div
                v-for="emp in filteredEmployees.slice(0, 60)"
                :key="emp.workNumber"
                class="member-search-item"
                :class="{
                  'is-disabled': !!emp.belongGroupName && !isInCurrentGroup(emp.workNumber),
                  'is-selected': isInCurrentGroup(emp.workNumber)
                }"
                @click="addMemberHandle(emp)"
              >
                <div class="member-search-item-main">
                  <div class="member-search-item-name">
                    {{ emp.nickName }} <span class="work-number">({{ emp.workNumber }})</span>
                  </div>
                  <div class="member-search-item-desc">
                    <template v-if="emp.belongGroupName && !isInCurrentGroup(emp.workNumber)">
                      不可选择：已在【{{ emp.belongGroupName }}】
                    </template>
                    <template v-else-if="isInCurrentGroup(emp.workNumber)">
                      已添加到当前小组
                    </template>
                    <template v-else>
                      点击添加到当前小组
                    </template>
                  </div>
                </div>
                <div
                  v-if="emp.belongGroupName && !isInCurrentGroup(emp.workNumber)"
                  class="member-search-item-tag"
                >
                  已在【{{ emp.belongGroupName }}】
                </div>
                <i
                  v-else-if="isInCurrentGroup(emp.workNumber)"
                  class="el-icon-check member-search-item-check"
                ></i>
              </div>
            </template>
            <div v-else class="member-search-empty">未搜索到匹配的员工</div>
          </div>
        </div>

        <!-- 已添加成员列表 -->
        <CustomTable
          :column="memberTableColumns"
          :table-data="memberTableData"
          border
          stripe
          size="small"
          empty-text="暂无成员，点击上方“添加成员”选择"
          class="member-table"
          :max-height="330"
        >
          <template #leader="{ row }">
            <el-radio
              v-model="currentGroup.leaderWorkNumber"
              :label="row.workNumber"
            >&nbsp;{{ currentGroup.leaderWorkNumber === row.workNumber ? '组长' : '设为组长' }}</el-radio>
          </template>
          <template #operation="{ row }">
            <el-link type="danger" @click="removeMemberHandle(row)">移除</el-link>
          </template>
        </CustomTable>

        <div class="member-rule-tip">
          规则：一个小组仅允许 1 名组长；同一成员仅允许在 1 个小组内。
        </div>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import request from '@/utils/request'
export default {
  name: 'AddOrEditGroup',
  props: {
    // 当前编辑的小组（来自父组件，直接复用引用，便于父组件读取最终值）
    currentGroup: {
      type: Object,
      required: true
    },
    // 现有小组列表（用于判断成员已属于哪个小组）
    groupList: {
      type: Array,
      default: () => ([])
    }
  },
  data() {
    return {
      showMemberPanel: false,
      showSearchList: true,
      searchKey: '',
      employeeOptions: [],
      memberTableData: [],
      memberTableColumns: [
        { type: 'index', label: '序号', width: 80, align: 'center' },
        { prop: 'nickName', label: '姓名', 'min-width': 100 },
        { prop: 'workNumber', label: '工号', 'min-width': 100 },
        { label: '是否为组长', 'min-width': 100, align: 'left', slotName: 'leader' },
        { label: '操作', 'min-width': 100, align: 'center', slotName: 'operation' }
      ],
      rules: {
        groupName: [
          { required: true, message: '请输入小组名称', trigger: 'blur' },
          { max: 20, message: '小组名称不超过 20 个字', trigger: 'blur' }
        ],
        memberWorkNumbers: [
          { required: true, message: '请至少添加 1 名成员', trigger: 'blur' },
        ]
      }
    }
  },
  methods: {
    // 获取员工列表（用于下拉选项）
    async getEmployeeOptions() {
      try {
        const params = {}
        const res = await request.get('/cluepool/pc/user/all', params, '')
        if (res.code === 200) {
          this.employeeOptions = (res.data || []).map(item => ({
            label: item.nickName,
            value: item.workNumber
          }))
        }
      } catch (error) {
        console.log('获取员工列表失败', error)
      }
    },

    // 获取组员列表（编辑模式）
    async getGroupMembers() {
      const groupInfoId = this.currentGroup.groupInfoId
      if (!groupInfoId) return
      const params = {
        groupInfoId
      }
      try {
        const res = await request.get('/cluepool/pc/admin/group/members', params, '')
        if (res.code === 200) {
          const members = res.data || []
          this.memberTableData = members.map(m => ({
            workNumber: m.workNumber,
            nickName: m.nickName
          }))
          this.currentGroup.memberWorkNumbers = members.map(m => m.workNumber)
          const leader = members.find(m => m.leader)
          this.currentGroup.leaderWorkNumber = leader ? leader.workNumber : ''
        }
      } catch (error) {
        console.log('获取组员列表失败', error)
      }
    },

    // 点击搜索面板外部区域时隐藏列表
    handleDocumentClick(e) {
      if (!this.showMemberPanel || !this.showSearchList) return
      const panel = this.$refs.memberSearchPanelRef
      if (panel && !panel.contains(e.target)) {
        this.showSearchList = false
        this.searchKey = ''
      }
    },

    isInCurrentGroup(workNumber) {
      return (this.currentGroup.memberWorkNumbers || []).includes(workNumber)
    },

    addMemberHandle(emp) {
      // 已被其他小组占用 -> 不可选
      if (emp.belongGroupName) {
        this.$message.warning(`【${emp.nickName}】已在【${emp.belongGroupName}】，不可重复添加`)
        return
      }
      const memberWorkNumbers = this.currentGroup.memberWorkNumbers || []
      if (memberWorkNumbers.includes(emp.workNumber)) {
        // 已添加 -> 再次点击移除（便于切换）
        this.removeMemberHandle(emp)
        return
      }
      this.currentGroup.memberWorkNumbers = [...memberWorkNumbers, emp.workNumber]
      this.memberTableData = [...this.memberTableData, { workNumber: emp.workNumber, nickName: emp.nickName }]
      this.$refs.formRef && this.$refs.formRef.clearValidate('memberWorkNumbers')
    },

    removeMemberHandle(row) {
      const memberWorkNumbers = this.currentGroup.memberWorkNumbers || []
      this.currentGroup.memberWorkNumbers = memberWorkNumbers.filter(id => id !== row.workNumber)
      this.memberTableData = this.memberTableData.filter(m => m.workNumber !== row.workNumber)
      // 若移除的是组长，则清空组长，由用户重新指定
      if (this.currentGroup.leaderWorkNumber === row.workNumber) {
        this.currentGroup.leaderWorkNumber = ''
      }
    },
    // 暴露给父组件调用
    validate(cb) {
      this.$refs.formRef.validate(cb)
    },
    resetFields() {
      this.$refs.formRef && this.$refs.formRef.resetFields()
    }
  },
  created() {
    this.getEmployeeOptions()
    if (this.currentGroup.groupInfoId) {
      this.getGroupMembers()
    }
  },
  computed: {
    // 员工 workNumber -> 所属小组名（排除当前编辑的小组自身）
    employeeBelongMap() {
      const map = {}
      const editingGroupInfoId = this.currentGroup.groupInfoId
      this.groupList.forEach(group => {
        if (editingGroupInfoId && group.groupInfoId === editingGroupInfoId) return
        (group.members || []).forEach(m => {
          map[m.workNumber] = group.groupName
        })
      })
      return map
    },
    // 全量员工（带所属小组信息）
    allEmployees() {
      return this.employeeOptions.map(opt => ({
        nickName: opt.label,
        workNumber: opt.value,
        belongGroupName: this.employeeBelongMap[opt.value] || ''
      }))
    },
    // 搜索过滤后的员工
    filteredEmployees() {
      const key = this.searchKey.trim().toLowerCase()
      if (!key) return this.allEmployees
      return this.allEmployees.filter(emp => {
        return (
          (emp.nickName || '').toLowerCase().includes(key) ||
          (emp.workNumber || '').toString().toLowerCase().includes(key)
        )
      })
    }
  },
  components: {
    CustomTable: () => import('@/components/CustomTable.vue')
  },
  watch: {
    // 父组件切换 currentGroup 引用（新增/编辑切换）时清掉残留校验状态
    'currentGroup.groupInfoId'(newVal) {
      this.showMemberPanel = false
      this.showSearchList = false
      this.searchKey = ''
      if (newVal) {
        this.getGroupMembers()
      } else {
        this.memberTableData = []
      }
      this.$nextTick(() => {
        this.$refs.formRef && this.$refs.formRef.clearValidate()
      })
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick)
  }
}
</script>

<style scoped lang="less">
.add-or-edit-group {
  min-height: 320px;

  .member-form-item {
    /deep/ .el-form-item__content {
      line-height: 1.5;
      position: relative;
    }
  }

  .member-toolbar {
    margin: 8px;
  }

  .member-search-panel {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background-color: #fff;
    margin-top: 4px;

    .member-search-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-bottom: 1px solid #ebeef5;

      .collapse-btn {
        flex-shrink: 0;
      }
    }

    .member-search-list {
      position: absolute;
      top: 86px;
      left: 10px;
      right: 0;
      z-index: 100;
      width: 615px;
      max-height: 240px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      overflow-y: auto;

      .member-search-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px;
        cursor: pointer;
        border-bottom: 1px solid #f5f7fa;
        background-color: #fff;
  
        &:last-child {
          border-bottom: none;
        }
  
        &:hover {
          background-color: #f5f7fa;
        }
  
        &.is-disabled {
          cursor: not-allowed;
          color: #c0c4cc;
        }
  
        &.is-selected {
          background-color: #ecf5ff;
  
          .member-search-item-name {
            color: #409eff;
          }
        }
  
        .member-search-item-main {
          flex: 1;
          min-width: 0;
        }
  
        .member-search-item-name {
          font-size: 14px;
          font-weight: 600;
          color: #303133;
  
          .work-number {
            font-weight: 400;
            color: #909399;
          }
        }
  
        &.is-disabled .member-search-item-name,
        &.is-disabled .member-search-item-name .work-number {
          color: #c0c4cc;
        }
  
        .member-search-item-desc {
          font-size: 12px;
          color: #909399;
          margin-top: 2px;
        }
  
        .member-search-item-tag {
          flex-shrink: 0;
          margin-left: 12px;
          padding: 2px 8px;
          font-size: 12px;
          color: #f56c6c;
          background-color: #fef0f0;
          border: 1px solid #fbc4c4;
          border-radius: 4px;
        }
  
        .member-search-item-check {
          flex-shrink: 0;
          margin-left: 12px;
          color: #409eff;
          font-size: 16px;
        }
      }
  
      .member-search-empty {
        padding: 16px;
        text-align: center;
        color: #909399;
        font-size: 13px;
        background-color: #fff;
      }
    }
  }

  .member-table {
    width: 100%;

    /deep/ .el-radio__label {
      padding-left: 0;
    }
  }

  .member-rule-tip {
    margin-top: 8px;
    font-size: 13px;
    color: #E6A23C;
  }
}
</style>
